import { BepoFloatingPlayer } from "./bepoFloatingPlayer"
import { disablePageRefresh, enablePageRefresh } from "../../../siteWidgets/siteWidgetUtils"
import { SiteVideoData } from "../../../interfaces"

declare var Hls: any

interface WcmAudioPlayerInstance {
    stop: () => void
    togglePlayPause?: () => void
}

declare var window: Window & {
    wcmAudioPlayer: WcmAudioPlayerInstance
    stopCurrentPlayingPlayer?: () => void
}

export interface BepoFloatingPlayerControllerProps {
    title: string
    podcastName: string
    podcastImageUrl: string
    podcastPageUrl: string
    bepoHomeUrl: string
    audioUrl: string
    videoData?: SiteVideoData
    hideVideoAds?: boolean
    mediaImageUrl?: string
    duration: number
    isMobileWeb?: boolean
    footerVariant?: "podcast" | "sponsor"
    onClose: () => void
    onPlayingChange?: (isPlaying: boolean) => void
}

interface BepoFloatingPlayerControllerState {
    isPlaying: boolean
    currentTime: number
    playbackSpeed: number
    isLoading: boolean
    audioDuration: number
    videoDuration: number
}

export class BepoFloatingPlayerController extends React.Component<
    BepoFloatingPlayerControllerProps,
    BepoFloatingPlayerControllerState
> {
    private audioElement: HTMLAudioElement = RUNNING_IN_SERVER ? null : new Audio()
    private videoElement: HTMLVideoElement = null
    private hls: any = null
    private isAudioElementInitialized = false
    private playerApiRef: { current: HTMLMediaElement | null } = { current: null }
    private shouldAutoPlayOnAttach = true
    private isShowingAd = false

    constructor(props: BepoFloatingPlayerControllerProps) {
        super(props)
        this.state = {
            isPlaying: false,
            currentTime: 0,
            playbackSpeed: 1,
            isLoading: false,
            audioDuration: 0,
            videoDuration: 0,
        }
    }

    private areVideoAdsEnabled = () => !this.props.hideVideoAds

    private isVideoAdActive = () => {
        if (!this.areVideoAdsEnabled()) {
            return false
        }
        if (this.isShowingAd) {
            return true
        }
        const root = this.videoElement
            && this.videoElement.closest
            && this.videoElement.closest(".flowplayer")
        if (!root || !root.classList) {
            return false
        }
        return root.classList.contains("is-ad-linear")
            || root.classList.contains("ad-linear")
            || root.classList.contains("ad-playing")
    }

    private handleAdPlayingChange = (isShowingAd: boolean) => {
        this.isShowingAd = isShowingAd
        if (!isShowingAd) {
            if (this.videoElement && this.videoElement.isConnected && !this.videoElement.paused) {
                this.setState({ isPlaying: true, isLoading: false })
            }
            return
        }
        // Ad UI: always show spinner — never a play button that looks like content is paused.
        this.setState({ isPlaying: false, isLoading: true })
    }

    componentDidMount() {
        if (RUNNING_IN_SERVER) return

        const { audioUrl, videoData } = this.props
        const isVideoMode = this.isVideoMode(videoData)

        if (audioUrl) {
            this.setupAudioElement(audioUrl)
            this.changeSrc(audioUrl)
            this.audioElement.setAttribute("data-src-loaded", "1")
            this.syncPlayerApiRef()
        }

        if (isVideoMode) {
            this.shouldAutoPlayOnAttach = true
            this.setState({ isLoading: true })
            return
        }

        if (!audioUrl) {
            return
        }

        this.registerAsSingleton()
        this.setState({ isLoading: true })
        const playPromise = this.audioElement.play()
        if (playPromise !== undefined) {
            playPromise.catch((error) => {
                console.error(error)
                this.setState({ isPlaying: false, isLoading: false })
            })
        }
    }

    componentWillUnmount() {
        if (RUNNING_IN_SERVER) return

        this.teardownAudioElement()
        this.detachVideoElement()

        if ((window.wcmAudioPlayer as any) === this) {
            window.wcmAudioPlayer = null
        }

        enablePageRefresh()
    }

    componentDidUpdate(prevProps: BepoFloatingPlayerControllerProps, prevState: BepoFloatingPlayerControllerState) {
        const { audioUrl, videoData, onPlayingChange } = this.props
        if (audioUrl !== prevProps.audioUrl && this.isAudioElementInitialized) {
            this.changeSrc(audioUrl)
        }

        const switchedToAudio = !this.isVideoMode(videoData) && this.isVideoMode(prevProps.videoData)
        if (switchedToAudio) {
            this.syncPlayerApiRef()
            this.setState({ currentTime: 0, isPlaying: false, isLoading: false, videoDuration: 0 })
        }

        const switchedToVideo = this.isVideoMode(videoData) && !this.isVideoMode(prevProps.videoData)
        const videoSourceChanged = this.isVideoMode(videoData) && this.isVideoMode(prevProps.videoData)
            && videoData && prevProps.videoData && videoData.url !== prevProps.videoData.url

        if (switchedToVideo || videoSourceChanged) {
            this.shouldAutoPlayOnAttach = true
            this.isShowingAd = false
            this.setState({ currentTime: 0, isPlaying: false, isLoading: true, videoDuration: 0 })
        }

        if (prevState.isPlaying !== this.state.isPlaying && onPlayingChange) {
            onPlayingChange(this.state.isPlaying)
        }
    }

    private setupAudioElement = (audioUrl: string) => {
        this.isAudioElementInitialized = true

        if (typeof Hls !== "undefined" && Hls.isSupported() && audioUrl.endsWith(".m3u8")) {
            this.hls = new Hls()
        }

        this.audioElement.addEventListener("play", this.handleAudioPlay)
        this.audioElement.addEventListener("playing", this.handleAudioPlaying)
        this.audioElement.addEventListener("waiting", this.handleAudioWaiting)
        this.audioElement.addEventListener("pause", this.handleAudioPause)
        this.audioElement.addEventListener("ended", this.handleAudioEnded)
        this.audioElement.addEventListener("timeupdate", this.handleAudioTimeUpdate)
        this.audioElement.addEventListener("loadedmetadata", this.handleAudioLoadedMetadata)
        this.audioElement.addEventListener("durationchange", this.handleAudioDurationChange)
        this.audioElement.addEventListener("error", this.handleAudioError)
    }

    private teardownAudioElement = () => {
        if (!this.isAudioElementInitialized) {
            return
        }

        this.audioElement.removeEventListener("play", this.handleAudioPlay)
        this.audioElement.removeEventListener("playing", this.handleAudioPlaying)
        this.audioElement.removeEventListener("waiting", this.handleAudioWaiting)
        this.audioElement.removeEventListener("pause", this.handleAudioPause)
        this.audioElement.removeEventListener("ended", this.handleAudioEnded)
        this.audioElement.removeEventListener("timeupdate", this.handleAudioTimeUpdate)
        this.audioElement.removeEventListener("loadedmetadata", this.handleAudioLoadedMetadata)
        this.audioElement.removeEventListener("durationchange", this.handleAudioDurationChange)
        this.audioElement.removeEventListener("error", this.handleAudioError)
        this.audioElement.pause()
        this.hls && this.hls.destroy()
        this.hls = null
        this.isAudioElementInitialized = false
    }

    private attachVideoElement = (videoElement: HTMLVideoElement) => {
        this.videoElement = videoElement
        this.videoElement.playbackRate = this.state.playbackSpeed
        this.videoElement.addEventListener("play", this.handleVideoPlay)
        this.videoElement.addEventListener("playing", this.handleVideoPlaying)
        this.videoElement.addEventListener("waiting", this.handleVideoWaiting)
        this.videoElement.addEventListener("pause", this.handleVideoPause)
        this.videoElement.addEventListener("ended", this.handleVideoEnded)
        this.videoElement.addEventListener("timeupdate", this.handleVideoTimeUpdate)
        this.videoElement.addEventListener("loadedmetadata", this.handleVideoLoadedMetadata)
        this.videoElement.addEventListener("error", this.handleVideoError)
        this.syncPlayerApiRef()
        if (this.isVideoAdActive()) {
            this.setState({
                currentTime: 0,
                videoDuration: this.videoElement.duration && this.videoElement.duration > 0 ? this.videoElement.duration : 0,
                isPlaying: false,
                isLoading: true,
            })
        } else {
            this.setState({
                currentTime: this.videoElement.currentTime || 0,
                videoDuration: this.videoElement.duration && this.videoElement.duration > 0 ? this.videoElement.duration : 0,
                isPlaying: !this.videoElement.paused,
                isLoading: this.videoElement.paused,
            })
        }

        if (this.shouldAutoPlayOnAttach) {
            this.shouldAutoPlayOnAttach = false
            this.registerAsSingleton()

            // When preroll is enabled, FloatingVideoLivePlayer-style flow owns playback:
            // Flowplayer.play() then ads.requestAd(); custom video.play() would race IMA.
            if (this.areVideoAdsEnabled()) {
                this.setState({
                    isLoading: true,
                    isPlaying: !this.videoElement.paused,
                })
                return
            }

            if (this.videoElement.paused) {
                this.setState({ isLoading: true })
                const playPromise = this.videoElement.play()
                if (playPromise !== undefined) {
                    playPromise.catch((error) => {
                        console.error(error)
                        this.setState({ isPlaying: false, isLoading: false })
                    })
                }
            }
        }
    }

    private detachVideoElement = () => {
        if (!this.videoElement) {
            return
        }

        this.videoElement.removeEventListener("play", this.handleVideoPlay)
        this.videoElement.removeEventListener("playing", this.handleVideoPlaying)
        this.videoElement.removeEventListener("waiting", this.handleVideoWaiting)
        this.videoElement.removeEventListener("pause", this.handleVideoPause)
        this.videoElement.removeEventListener("ended", this.handleVideoEnded)
        this.videoElement.removeEventListener("timeupdate", this.handleVideoTimeUpdate)
        this.videoElement.removeEventListener("loadedmetadata", this.handleVideoLoadedMetadata)
        this.videoElement.removeEventListener("error", this.handleVideoError)
        this.videoElement = null
        this.syncPlayerApiRef()
    }

    private isVideoMode = (videoData?: SiteVideoData) => !!(videoData && videoData.url)

    private syncPlayerApiRef = () => {
        if (this.isVideoMode(this.props.videoData) && this.videoElement) {
            this.playerApiRef.current = this.videoElement
            return
        }

        if (this.isAudioElementInitialized && this.audioElement) {
            this.playerApiRef.current = this.audioElement
            return
        }

        this.playerApiRef.current = null
    }

    private setVideoElement = (videoElement: HTMLVideoElement) => {
        if (this.videoElement === videoElement) {
            return
        }

        this.detachVideoElement()

        if (videoElement) {
            this.attachVideoElement(videoElement)
            return
        }

        this.setState({ isPlaying: false, isLoading: false, currentTime: 0, videoDuration: 0 })
    }

    private getActiveDuration = () => {
        const { duration, videoData } = this.props
        if (this.isVideoMode(videoData)) {
            if (this.state.videoDuration > 0) {
                return this.state.videoDuration
            }
            if (videoData && videoData.duration && videoData.duration > 0) {
                return videoData.duration
            }
            return 0
        }

        if (this.state.audioDuration > 0) {
            return this.state.audioDuration
        }
        return duration
    }

    private registerAsSingleton = () => {
        if (window.wcmAudioPlayer && window.wcmAudioPlayer !== (this as any)) {
            window.wcmAudioPlayer.stop()
        }
        window.wcmAudioPlayer = this as any
    }

    private changeSrc = (newSrc: string) => {
        const { isPlaying } = this.state
        if (this.hls) {
            this.hls.destroy()
            this.hls = new Hls()
            this.hls.loadSource(newSrc)
            this.hls.attachMedia(this.audioElement)
        } else {
            this.audioElement.src = newSrc
        }

        if (isPlaying) {
            this.audioElement.play()
        }
    }

    public stop = () => {
        const { videoData } = this.props
        const { isPlaying } = this.state
        const isVideoMode = this.isVideoMode(videoData)

        if (isVideoMode && this.videoElement) {
            if (isPlaying) {
                this.videoElement.pause()
            }
            this.videoElement.currentTime = 0
            this.setState({ isPlaying: false, currentTime: 0, isLoading: false })
            enablePageRefresh()
            this.props.onClose()
            return
        }

        if (isPlaying) {
            this.audioElement.pause()
        }
        this.audioElement.currentTime = 0

        this.setState({ isPlaying: false, currentTime: 0, isLoading: false })
        enablePageRefresh()
        this.props.onClose()
    }

    private handlePlayPause = () => {
        const { audioUrl, videoData } = this.props
        const { isPlaying } = this.state

        if (this.isVideoMode(videoData)) {
            // During preroll the content <video> may be detached by IMA — never call play/pause on it.
            if (this.isVideoAdActive()) {
                return
            }
            if (!this.videoElement || !this.videoElement.isConnected) {
                return
            }
            if (isPlaying) {
                this.videoElement.pause()
            } else {
                this.registerAsSingleton()
                this.setState({ isLoading: true })
                const playPromise = this.videoElement.play()
                if (playPromise !== undefined) {
                    playPromise.catch((error) => {
                        console.error(error)
                        this.setState({ isPlaying: false, isLoading: false })
                    })
                }
            }
            return
        }

        if (isPlaying) {
            this.audioElement.pause()
        } else {
            this.registerAsSingleton()
            if (!audioUrl) {
                console.warn("BepoFloatingPlayerController: audioUrl is empty, cannot play")
                return
            }
            this.setState({ isLoading: true })
            if (!this.audioElement.getAttribute("data-src-loaded")) {
                this.audioElement.setAttribute("data-src-loaded", "1")
                this.changeSrc(audioUrl)
            }
            const playPromise = this.audioElement.play()
            if (playPromise !== undefined) {
                playPromise.catch((error) => {
                    console.error(error)
                    this.setState({ isPlaying: false, isLoading: false })
                })
            }
        }
    }

    public togglePlayPause = () => {
        this.handlePlayPause()
    }

    private handleSeek = (time: number) => {
        const { videoData } = this.props
        if (this.isVideoMode(videoData) && this.videoElement) {
            if (this.isVideoAdActive()) {
                return
            }
            this.videoElement.currentTime = time
            this.setState({ currentTime: time })
            return
        }

        this.audioElement.currentTime = time
        this.setState({ currentTime: time })
    }

    private handleForward = () => {
        const newTime = this.state.currentTime + 15
        this.handleSeek(Math.min(newTime, this.getActiveDuration()))
    }

    private handleBackward = () => {
        const newTime = this.state.currentTime - 15
        this.handleSeek(Math.max(newTime, 0))
    }

    private handleAudioPlay = () => {
        this.setState({ isPlaying: true })
        disablePageRefresh()
    }

    private handleAudioPlaying = () => {
        this.setState({ isLoading: false })
    }

    private handleAudioWaiting = () => {
        this.setState({ isLoading: true })
    }

    private handleAudioPause = () => {
        this.setState({ isPlaying: false, isLoading: false })
        enablePageRefresh()
    }

    private handleAudioEnded = () => {
        this.setState({ isPlaying: false, currentTime: 0 })
        enablePageRefresh()
    }

    private handleAudioTimeUpdate = () => {
        this.setState({ currentTime: this.audioElement.currentTime })
    }

    private handleAudioLoadedMetadata = () => {
        this.setState({ audioDuration: this.getSafeAudioDuration() })
    }

    private handleAudioDurationChange = () => {
        this.setState({ audioDuration: this.getSafeAudioDuration() })
    }

    private getSafeAudioDuration = () => {
        const value = this.audioElement && this.audioElement.duration
        return typeof value === "number" && isFinite(value) && value > 0 ? value : 0
    }

    private handleAudioError = () => {
        this.setState({ isPlaying: false, isLoading: false })
    }

    private handleVideoPlay = () => {
        if (this.isVideoAdActive()) {
            this.setState({ isPlaying: false, isLoading: true })
            return
        }
        this.setState({ isPlaying: true })
        disablePageRefresh()
        this.registerAsSingleton()
        if (this.isAudioElementInitialized && !this.audioElement.paused) {
            this.audioElement.pause()
        }
    }

    private handleVideoPlaying = () => {
        if (this.isVideoAdActive()) {
            this.setState({ isPlaying: false, isLoading: true })
            return
        }
        this.setState({ isLoading: false })
    }

    private handleVideoWaiting = () => {
        this.setState({ isLoading: true })
    }

    private handleVideoPause = () => {
        if (this.isVideoAdActive()) {
            // IMA pauses the content engine while the ad plays — keep spinner, not play icon.
            this.setState({ isPlaying: false, isLoading: true })
            return
        }
        this.setState({ isPlaying: false, isLoading: false })
        enablePageRefresh()
    }

    private handleVideoEnded = () => {
        // IMA can fire "ended" on the shared video element when the preroll finishes.
        if (this.isVideoAdActive()) {
            this.setState({ isPlaying: false, isLoading: true })
            return
        }
        this.setState({ isPlaying: false, currentTime: 0 })
        enablePageRefresh()
    }

    private handleVideoTimeUpdate = () => {
        if (this.isVideoAdActive()) {
            return
        }
        this.videoElement && this.setState({ currentTime: this.videoElement.currentTime || 0 })
    }

    private handleVideoLoadedMetadata = () => {
        this.videoElement && this.setState({ videoDuration: this.videoElement.duration || 0 })
    }

    private handleVideoError = () => {
        if (this.isVideoAdActive()) {
            this.setState({ isPlaying: false, isLoading: true })
            return
        }
        this.setState({ isPlaying: false, isLoading: false })
    }


    render() {
        const {
            title,
            podcastName,
            podcastImageUrl,
            podcastPageUrl,
            bepoHomeUrl,
            audioUrl,
            videoData,
            hideVideoAds,
            mediaImageUrl,
            duration,
            isMobileWeb,
            onClose,
            footerVariant,
        } = this.props

        const { isPlaying, currentTime, playbackSpeed, isLoading } = this.state

        const isVideoMode = this.isVideoMode(videoData)
        const hasMedia = !!mediaImageUrl || isVideoMode
        const activeDuration = this.getActiveDuration()
        const adActive = this.isVideoAdActive()

        return (
            <BepoFloatingPlayer
                title={title}
                podcastName={podcastName}
                podcastImageUrl={podcastImageUrl}
                podcastPageUrl={podcastPageUrl}
                bepoHomeUrl={bepoHomeUrl}
                hasMedia={hasMedia}
                videoData={videoData}
                hideVideoAds={hideVideoAds}
                isVideoMode={isVideoMode}
                mediaImageUrl={mediaImageUrl}
                isVisible={true}
                isPlaying={adActive ? false : isPlaying}
                isLoading={adActive ? true : isLoading}
                currentTime={currentTime}
                duration={activeDuration}
                playbackSpeed={playbackSpeed}
                isMobileWeb={isMobileWeb}
                playerApiRef={this.playerApiRef}
                footerVariant={footerVariant}
                onClose={onClose}
                onPlayPause={this.handlePlayPause}
                onSeek={this.handleSeek}
                onForward={this.handleForward}
                onBackward={this.handleBackward}
                onVideoElementChange={this.setVideoElement}
                onAdPlayingChange={this.handleAdPlayingChange}
            />
        )
    }
}
